import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ModelViewer from '../components/webVR/ModelViewer';
import GlowEffect from '../components/webVR/GlowEffect';

export default function WebVR() {
  const [audioPlaying, setAudioPlaying] = useState(false);

  const handleEnterVR = () => {
    // Get the scene element
    const scene = document.querySelector('a-scene');
    if (scene) {
      // Start the VR experience
      const aframeScene = scene as any;
      
      // Toggle VR mode
      if (aframeScene.is && aframeScene.is('vr-mode')) {
        aframeScene.exitVR();
      } else {
        aframeScene.enterVR();
      }
      
      // Play or pause the ocean ambient sound
      const oceanAudio = document.querySelector('#ocean-ambient') as HTMLAudioElement;
      if (oceanAudio) {
        if (!audioPlaying) {
          // Play the audio
          oceanAudio.volume = 0.5;
          oceanAudio.currentTime = 0; // Start from beginning
          oceanAudio.play().then(() => {
            setAudioPlaying(true);
            console.log('Audio started playing successfully');
          }).catch(error => {
            console.warn('Audio playback failed:', error);
          });
        } else {
          // Pause the audio
          oceanAudio.pause();
          setAudioPlaying(false);
        }
      } else {
        console.warn('Audio element with ID "ocean-ambient" not found');
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>WebVR | BIOCAP Website</title>
        <meta name="description" content="Explore BIOCAP's coastal protection solutions in 3D with our immersive WebVR experience" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h1 className="text-4xl font-bold text-[#005E6E] mb-4">Immersive WebXR Experience</h1>
            <p className="text-gray-600 text-lg mb-6">
              Step into our interactive WebXR environment to explore BIOCAP's coastal protection solutions. 
              Navigate freely through the scene, observe ocean dynamics, and experience the beauty 
              of sustainable marine engineering from any device or VR headset.
            </p>
            <div className="space-y-4">
              <button 
                className="bg-[#005E6E] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#004a58] transition-all shadow-md flex items-center justify-center gap-2 w-full sm:w-auto mb-4"
                onClick={handleEnterVR}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 0 0 3.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0 1 20.25 6v1.5m0 9V18A2.25 2.25 0 0 1 18 20.25h-1.5m-9 0H6A2.25 2.25 0 0 1 3.75 18v-1.5M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
                {audioPlaying ? "Exit Immersive Mode" : "Enter Immersive Experience"}
              </button>
              


              <div className="bg-gradient-to-r from-sky-50 to-teal-50 rounded-lg p-4 shadow-sm border border-teal-100">
                
                {/* Feature badges */}
                <div className="mt-5 flex flex-wrap gap-2">
                  <div className="flex flex-col items-center bg-white p-3 rounded-lg shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-teal-600 mb-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700">Mobile Friendly</span>
                  </div>
                  
                  <div className="flex flex-col items-center bg-white p-3 rounded-lg shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-teal-600 mb-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700">Cross-platform</span>
                  </div>
                  
                  <div className="flex flex-col items-center bg-white p-3 rounded-lg shadow-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-teal-600 mb-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700">Immersive Audio</span>
                  </div>
                </div>
                
                <div className="mt-8"></div>
                <h3 className="font-semibold text-slate-700 text-lg mb-3">Controls:</h3>
                <ul className="text-slate-600 space-y-2">
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-teal-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672Zm-7.518-.267A8.25 8.25 0 1 1 20.25 10.5M8.288 14.212A5.25 5.25 0 1 1 17.25 10.5" />
                    </svg>
                    <span>Mouse drag to look around</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-teal-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                    </svg>
                    <span>Arrow keys or WASD to move</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-teal-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l7.5-7.5 7.5 7.5m-15 6l7.5-7.5 7.5 7.5" />
                    </svg>
                    <span>Q/E keys to move up/down</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-teal-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672Zm-7.518-.267A8.25 8.25 0 1 1 20.25 10.5M8.288 14.212A5.25 5.25 0 1 1 17.25 10.5" />
                    </svg>
                    <span>Click in the scene to activate keyboard controls</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-teal-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 0 0 3.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0 1 20.25 6v1.5m0 9V18A2.25 2.25 0 0 1 18 20.25h-1.5m-9 0H6A2.25 2.25 0 0 1 3.75 18v-1.5M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                    <span>VR button for immersive mode with ocean sounds</span>
                  </li>
                </ul>
                
                
              </div>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden h-full shadow-xl">
            <GlowEffect radius={75} intensity={0.85} useGradient={true}>
              <ModelViewer />
            </GlowEffect>
          </div>
        </div>
      </div>
    </>
  );
} 